/**
 * Drive-time polygons / isochrones (M8-B). Resolves an origin, requests
 * isochrone bands through the vendor-abstracted `RoutingProvider.getIsochrones`
 * (the stub isochrone provider by default), and persists band + origin refs in
 * `analysis.driveTime`. Each band carries a persisted token color so the fill
 * renders byte-identically in the editor and the M6 embed (no token lookup at
 * render time — the same posture as territory colors).
 *
 * The default provider returns a clearly-labeled STRAIGHT-LINE APPROXIMATION;
 * `buildDriveTime` returns that `approximate` flag so the UI can surface "until
 * a routing engine is configured". Real road-network isochrones are a deploy-
 * time concern — binding a Valhalla/OSRM provider is a one-place swap.
 *
 * Metered posture: the caller passes the M7 entitlement gate before invoking.
 */

import type { PointSet } from "@/lib/map/pointset";
import type { IsochroneBand, LngLat, RoutingProvider } from "@/lib/geo/routing";
import { centroid } from "./geometry";
import { territoryColor } from "./territories";
import type { DriveTime, DriveTimeBand } from "./spec";

/** Persisted per-band fill opacity (unitless float — not a spacing/color token). */
export const DRIVE_TIME_FILL_OPACITY = 0.14;

/** What the origin should persist as: explicit row ids, or the "selected" set. */
export type DriveTimeOriginRefs = string[] | "selected";

/**
 * Resolve the origin refs to a single coordinate to compute isochrones FROM:
 * the centroid of the referenced rows, or the whole-set centroid for
 * "selected" when no explicit rows are given. Returns null when there are no
 * points to anchor on.
 */
export function resolveDriveTimeOrigin(pointSet: PointSet, originRefs: DriveTimeOriginRefs): LngLat | null {
  const ids = originRefs === "selected" ? [] : originRefs;
  const rows = ids.length > 0 ? pointSet.points.filter((p) => ids.includes(p.id)) : pointSet.points;
  return centroid(rows);
}

export interface DriveTimeBuildResult {
  driveTime: DriveTime;
  /** True when the bands are a straight-line approximation (default provider). */
  approximate: boolean;
  attribution: string;
}

/**
 * Build the `analysis.driveTime` block: request isochrones for `bands` from the
 * resolved origin, color each band from the token palette, and persist the
 * origin refs. Returns null-safe (an unresolvable origin yields empty bands).
 */
export async function buildDriveTime(
  pointSet: PointSet,
  originRefs: DriveTimeOriginRefs,
  bands: IsochroneBand[],
  provider: RoutingProvider,
  opts?: { palette?: string[] },
): Promise<DriveTimeBuildResult> {
  const palette = opts?.palette ?? [];
  const origin = resolveDriveTimeOrigin(pointSet, originRefs);
  if (!origin) {
    return { driveTime: { originRefs, bands: [] }, approximate: false, attribution: provider.attribution };
  }
  const isochrones = await provider.getIsochrones(origin, bands);
  let approximate = false;
  const outBands: DriveTimeBand[] = isochrones.map((iso, i) => {
    if (iso.approximate) approximate = true;
    const band: DriveTimeBand = {
      polygon: iso.polygon,
      style: { color: territoryColor(palette, i), opacity: DRIVE_TIME_FILL_OPACITY },
    };
    if (typeof iso.band.minutes === "number") band.minutes = iso.band.minutes;
    if (typeof iso.band.miles === "number") band.miles = iso.band.miles;
    return band;
  });
  return { driveTime: { originRefs, bands: outBands }, approximate, attribution: provider.attribution };
}

/** Human caption for a band ("20 min" / "5 mi") — display only. */
export function driveTimeBandLabel(band: DriveTimeBand, index: number): string {
  if (typeof band.minutes === "number") return `${band.minutes} min`;
  if (typeof band.miles === "number") return `${band.miles} mi`;
  return `Band ${index + 1}`;
}
